import { button as uiButton } from "./ui/button.js";
import { disclosure } from "./ui/disclosure.js";
import { createWorkspaceState, workspaceViewKey } from "./workspace-state.js";

const el = (tag, text, cls) => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (cls) node.className = cls;
  return node;
};
export function terminalText(text) {
  return (
    text
      // biome-ignore lint/suspicious/noControlCharactersInRegex: Strip OSC/CSI and controls from untrusted PTY output before text rendering.
      .replace(/\x1b\][^\x07]*(?:\x07|\x1b\\)|\x1b\[[0-?]*[ -/]*[@-~]/g, "")
      .replace(/\r\n/g, "\n")
      .split("")
      .filter(
        (char) =>
          char === "\n" ||
          char === "\t" ||
          (char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127),
      )
      .join("")
  );
}
export function createTerminal({
  client,
  uuid,
  getContext,
  report,
  onApply,
  onPreview,
}) {
  const views = createWorkspaceState();
  const transport = client;
  client = new Proxy(transport, {
    get(target, name) {
      const method = target[name];
      if (typeof method !== "function") return method;
      return async (...args) => {
        const current = generation;
        const result = await method.apply(target, args);
        if (current !== generation) throw new Error("Workspace changed.");
        return result;
      };
    },
  });
  let key = "",
    generation = 0,
    session,
    sessions = [],
    review,
    timer,
    disconnected = false,
    busy = false;
  const panel = el("section", undefined, "persistent-terminal"),
    heading = el("div", undefined, "terminal-heading"),
    title = el("strong", "Shell"),
    state = el("span", "No session", "muted"),
    actions = el("div", undefined, "actions"),
    options = disclosure({
      label: "Session options",
      align: "end",
      className: "terminal-options",
    }),
    secondaryActions = el("div", undefined, "actions"),
    optionContent = options.querySelector(".ui-disclosure__content"),
    output = el(
      "pre",
      "Start a shell to keep processes and edits across reconnects.",
      "terminal-output",
    ),
    form = el("form", undefined, "terminal-form"),
    label = el("label", "Command"),
    input = el("input"),
    send = uiButton({ text: "Send", variant: "primary", size: "sm" }),
    diff = el("div", undefined, "terminal-diff");
  const sessionLabel = el("label", "Session"),
    sessionPicker = el("select"),
    controlStatus = el("p", "", "muted"),
    sharing = el("dialog"),
    shareForm = el("form"),
    personLabel = el("label", "Person"),
    person = el("select"),
    permissionLabel = el("label", "Access"),
    permission = el("select"),
    invite = uiButton({ text: "Share", variant: "secondary", size: "sm" }),
    grants = el("div"),
    footnote = el("p", "", "dock-footnote");
  let grantsKey = "";
  sessionLabel.append(sessionPicker);
  personLabel.append(person);
  permissionLabel.append(permission);
  for (const [value, name] of [
    ["watch", "Watch output"],
    ["control", "Watch and control"],
  ]) {
    const option = el("option", name);
    option.value = value;
    permission.append(option);
  }
  invite.type = "submit";
  sharing.setAttribute("aria-label", "Share terminal");
  const closeSharing = uiButton({
    text: "Done",
    variant: "secondary",
    size: "sm",
  });
  closeSharing.type = "button";
  closeSharing.addEventListener("click", () => sharing.close());
  const shareHeading = el("div", undefined, "dialog-heading");
  shareHeading.append(el("h2", "Share terminal"), closeSharing);
  shareForm.append(personLabel, permissionLabel, invite);
  sharing.append(
    shareHeading,
    el(
      "p",
      "Invited people can read this session's existing and future output. Control also allows commands and file changes. Only you can review and apply changes.",
      "dock-footnote",
    ),
    shareForm,
    grants,
  );
  input.maxLength = 4000;
  input.autocomplete = "off";
  input.spellcheck = false;
  label.append(input);
  send.type = "submit";
  form.append(label, send);
  output.setAttribute("role", "log");
  output.setAttribute("aria-label", "Persistent shell output");
  optionContent.append(sessionLabel, secondaryActions, footnote);
  heading.append(title, state, options);
  panel.append(heading, actions, controlStatus, output, form, diff, sharing);
  document.getElementById("dock-terminal").prepend(panel);
  function params() {
    const c = getContext();
    if (!c.workspaceId) throw new Error("Choose a project first.");
    return {
      tenantId: c.tenantId,
      workspaceId: c.workspaceId,
      terminalId: session?.id,
    };
  }
  function button(text, fn) {
    const b = uiButton({ text, size: "sm" });
    b.type = "button";
    b.addEventListener("click", () => {
      if (options.contains(b)) options.open = false;
      void act(fn);
    });
    actions.append(b);
    return b;
  }
  const open = button("Start shell", async () => {
    const c = getContext();
    session = await client.openWorkspaceSession(params(), {
      requestId: uuid(),
      expectedSourceHash: c.sourceHash,
    });
    review = undefined;
  });
  const freeze = button("Freeze & review", async () => {
    session = await client.freezeWorkspaceSession(params());
    review = await client.reviewWorkspaceSession(params());
  });
  const discard = button("Discard session", async () => {
    session = await client.closeWorkspaceSession(params());
    review = undefined;
  });
  const refresh = button("Reconnect", async () => load());
  const preview = button("Open preview", () => onPreview?.());
  const take = button("Take control", () => changeControl("take"));
  const release = button("Release control", () => changeControl("release"));
  async function changeControl(action) {
    session = await client.controlWorkspaceSession(params(), {
      action,
      expectedRevision: session.access.revision,
    });
  }
  sessionPicker.addEventListener("change", () => {
    const id = sessionPicker.value;
    void act(async () => {
      session = await client.getWorkspaceSession({
        ...params(),
        terminalId: id,
      });
      review = undefined;
    });
  });
  const shareButton = button("Share terminal", async () => {
    sharing.showModal();
    const result = await client.listMembers({ tenantId: params().tenantId });
    person.replaceChildren();
    for (const member of result.items) {
      if (
        member.userId === getContext().userId ||
        !["owner", "admin"].includes(member.role)
      )
        continue;
      const option = el("option", member.displayName);
      option.value = member.userId;
      person.append(option);
    }
    if (!person.options.length) {
      const empty = el("option", "No other admins in this team");
      empty.value = "";
      person.append(empty);
    }
  });
  secondaryActions.append(shareButton, refresh, discard);
  shareForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void act(async () => {
      session = await client.shareWorkspaceSession(params(), {
        userId: person.value,
        permission: permission.value,
      });
    });
  });
  const keys = el("details", undefined, "terminal-keys"),
    keyActions = el("div", undefined, "actions");
  keys.append(
    el("summary", "Session controls"),
    keyActions,
    el(
      "p",
      "Preview server: 127.0.0.1:43110. One shell at a time, no network or credentials; 15-minute idle / 1-hour maximum.",
      "dock-footnote",
    ),
  );
  form.after(keys);
  for (const [name, data] of [
    ["Interrupt", "\x03"],
    ["Tab", "\t"],
    ["↑", "\x1b[A"],
    ["↓", "\x1b[B"],
  ]) {
    const b = button(name, () => write(data));
    b.dataset.shellKey = "true";
    keyActions.append(b);
  }
  const inputs = new Map();
  async function write(data) {
    const p = params(),
      key = JSON.stringify([p.tenantId, p.workspaceId, p.terminalId, data]);
    if (!inputs.has(key))
      inputs.set(key, {
        requestId: uuid(),
        data,
        ...(session?.access?.shared
          ? { expectedRevision: session.access.revision }
          : {}),
      });
    session = await client.inputWorkspaceSession(p, inputs.get(key));
    inputs.delete(key);
  }
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = input.value;
    void act(async () => {
      await write(`${data}\n`);
      input.value = "";
    });
  });
  async function act(fn) {
    if (busy) return;
    busy = true;
    const g = ++generation;
    clearTimeout(timer);
    render();
    try {
      await fn();
      if (g !== generation) return;
      render();
      poll();
    } catch (error) {
      if (g === generation) failed(error);
    } finally {
      if (g === generation) {
        busy = false;
        render();
        poll();
      }
    }
  }
  function failed(error) {
    if (!disconnected) report(error);
    disconnected = true;
    if ([401, 403, 404].includes(error.status)) {
      session = review = undefined;
      sessions = [];
      if (sharing.open) sharing.close();
      inputs.clear();
      input.value = "";
    }
  }
  function render() {
    if (session)
      views.write(workspaceViewKey(getContext()), { terminalId: session.id });
    if (session) {
      const index = sessions.findIndex((item) => item.id === session.id);
      if (index === -1) sessions.unshift(session);
      else sessions[index] = session;
    }
    const c = getContext(),
      running = session?.status === "running",
      owner = session?.access?.owner ?? true,
      controlling = session?.access?.controlling ?? true;
    state.textContent = disconnected
      ? "Reconnecting…"
      : session
        ? session.status
        : "No session";
    sessionLabel.hidden = sessions.length < 2;
    sessionPicker.disabled = busy;
    if (
      JSON.stringify(sessions.map((s) => [s.id, s.status])) !==
      sessionPicker.dataset.items
    ) {
      sessionPicker.dataset.items = JSON.stringify(
        sessions.map((s) => [s.id, s.status]),
      );
      sessionPicker.replaceChildren();
      for (const item of sessions) {
        const option = el(
          "option",
          `${item.access?.ownerName || "Shell"} · ${item.status} · ${new Date(item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        );
        option.value = item.id;
        sessionPicker.append(option);
      }
    }
    sessionPicker.value = session?.id || "";
    const access = session?.access;
    controlStatus.hidden = !access?.shared;
    controlStatus.textContent = access?.shared
      ? access.controlPending
        ? "Control is not confirmed. Reconnect or take control again."
        : controlling
          ? "You control this terminal."
          : access.controllerName
            ? `Watching · ${access.controllerName} controls this terminal.`
            : "Watching · nobody has control."
      : "";
    take.hidden =
      !running || !access?.shared || !access.canControl || controlling;
    take.textContent =
      owner && access?.controllerName ? "Take back control" : "Take control";
    take.disabled = busy || disconnected;
    release.hidden = !running || !access?.shared || !controlling;
    release.disabled = busy || disconnected;
    const canShare = owner && access && (running || access.grants.length > 0);
    shareButton.hidden = !canShare;
    shareButton.disabled = busy || disconnected;
    if (sharing.open && !canShare) sharing.close();
    shareForm.hidden = !running;
    invite.disabled = busy || !person.value || disconnected;
    person.disabled = permission.disabled = busy;
    options.hidden = !c.workspaceId || !c.allowed;
    footnote.textContent = owner
      ? "Edits stay in this session until you review and apply them."
      : `Shared by ${access?.ownerName || "the shell's creator"}. Review, apply and preview access stay with them.`;
    const nextGrants = JSON.stringify([session?.id, access?.grants]);
    if (nextGrants !== grantsKey) {
      grantsKey = nextGrants;
      grants.replaceChildren();
      for (const grant of access?.grants || []) {
        const row = el("div", undefined, "actions"),
          remove = uiButton({
            text: "Remove access",
            variant: "secondary",
            size: "sm",
          });
        remove.type = "button";
        remove.addEventListener(
          "click",
          () =>
            void act(async () => {
              session = await client.shareWorkspaceSession(params(), {
                userId: grant.userId,
                permission: "none",
              });
            }),
        );
        row.append(
          el(
            "span",
            `${grant.name} · ${grant.permission === "control" ? "Can control" : "Watching"}`,
          ),
          remove,
        );
        grants.append(row);
      }
    }
    for (const button of grants.querySelectorAll("button"))
      button.disabled = busy;
    const followOutput =
      output.scrollHeight - output.scrollTop - output.clientHeight < 32;
    output.textContent = session
      ? terminalText(
          session.output || session.error || `Session ${session.status}`,
        )
      : c.workspaceId
        ? "Start a shell to keep processes and edits across reconnects."
        : "Choose a project to start a terminal.";
    if (followOutput || output.dataset.session !== session?.id)
      output.scrollTop = output.scrollHeight;
    output.dataset.session = session?.id || "";
    open.disabled =
      busy ||
      !c.workspaceId ||
      !c.sourceHash ||
      !c.allowed ||
      ["running", "starting", "frozen"].includes(session?.status);
    open.hidden = ["starting", "running", "frozen"].includes(session?.status);
    // PANEL-04: a disabled Start shell says why.
    open.title = !c.workspaceId
      ? "Choose a project with a workspace to start a shell"
      : !c.allowed
        ? "Only admins can start a shell in this workspace"
        : !c.sourceHash
          ? "Waiting for the workspace to load"
          : "";
    freeze.hidden = !owner || !running;
    preview.hidden = !onPreview || !owner || !running;
    preview.disabled = busy || disconnected;
    discard.hidden =
      !owner || !["starting", "running", "frozen"].includes(session?.status);
    refresh.hidden = !c.workspaceId || !c.allowed;
    refresh.textContent = session ? "Reconnect" : "Refresh sessions";
    form.hidden = !running || !controlling;
    keys.hidden = !running || !controlling;
    freeze.disabled = busy || !running;
    discard.disabled =
      busy || !["running", "starting", "frozen"].includes(session?.status);
    refresh.disabled = busy || !c.workspaceId;
    input.disabled = send.disabled =
      busy || disconnected || !running || !controlling;
    for (const b of keyActions.querySelectorAll("[data-shell-key]"))
      b.disabled = busy || disconnected || !running || !controlling;
    diff.replaceChildren();
    if (owner && session?.status === "frozen" && !review) {
      const b = uiButton({
        text: "Load reviewed changes",
        variant: "secondary",
        size: "sm",
      });
      b.type = "button";
      b.addEventListener(
        "click",
        () =>
          void act(async () => {
            review = await client.reviewWorkspaceSession(params());
          }),
      );
      diff.append(b);
    }
    if (review) {
      diff.append(el("h3", `${review.files.length} changed files`));
      for (const file of review.files) {
        const detail = el("details"),
          sum = el(
            "summary",
            `${file.before === null ? "Added" : file.after === null ? "Deleted" : "Modified"} · ${file.path}`,
          );
        detail.append(sum);
        for (const [name, content] of [
          ["Before", file.before],
          ["After", file.after],
        ])
          detail.append(
            el("h4", name),
            el(
              "pre",
              content === null
                ? "(absent)"
                : file.encoding === "base64"
                  ? "Binary file (base64)\n" + content
                  : content,
            ),
          );
        diff.append(detail);
      }
      const apply = uiButton({
        text: "Apply reviewed changes",
        variant: "primary",
        size: "sm",
      });
      apply.type = "button";
      apply.disabled = busy || session?.status !== "frozen";
      apply.addEventListener(
        "click",
        () =>
          void act(async () => {
            session = await client.applyWorkspaceSession(params(), {
              requestId: uuid(),
              expectedSourceHash: review.baseHash,
              reviewHash: review.reviewHash,
            });
            review = undefined;
            await onApply();
          }),
      );
      diff.append(
        apply,
        el(
          "p",
          "Applies this frozen snapshot only if saved files still match the starting version.",
          "muted",
        ),
      );
    }
  }
  async function load() {
    const g = generation,
      p = params(),
      rows = await client.listWorkspaceSessions(p);
    if (g !== generation) return;
    sessions = rows;
    session =
      rows.find((s) => s.id === session?.id) ||
      rows.find(
        (s) => s.id === views.read(workspaceViewKey(getContext())).terminalId,
      ) ||
      rows.find((s) => s.status === "running") ||
      rows[0];
    review = undefined;
    if (session)
      session = await client.getWorkspaceSession({
        ...p,
        terminalId: session.id,
      });
    if (g !== generation) return;
    disconnected = false;
    render();
    poll();
  }
  function poll() {
    clearTimeout(timer);
    if (!session || !["running", "starting"].includes(session.status)) return;
    const g = generation,
      p = params();
    timer = setTimeout(
      () =>
        void (async () => {
          try {
            const loaded = await client.getWorkspaceSession(p);
            if (g !== generation) return;
            session = loaded;
            disconnected = false;
            if (
              session.access?.shared &&
              session.access.controlling &&
              session.access.leaseExpiresAt - Date.now() < 15_000 &&
              document.visibilityState === "visible" &&
              panel.getClientRects().length
            ) {
              await changeControl("renew");
              if (g !== generation) return;
            }
            render();
            poll();
          } catch (error) {
            if (g === generation) {
              failed(error);
              render();
              poll();
            }
          }
        })(),
      1500,
    );
  }
  return {
    update() {
      const c = getContext(),
        next = `${c.tenantId || ""}/${c.workspaceId || ""}/${c.userId || ""}/${c.allowed}`;
      if (next !== key) {
        key = next;
        generation++;
        busy = false;
        session = review = undefined;
        sessions = [];
        disconnected = false;
        if (sharing.open) sharing.close();
        person.replaceChildren();
        inputs.clear();
        clearTimeout(timer);
        if (c.workspaceId && c.allowed) void act(load);
      }
      render();
    },
    get session() {
      return session;
    },
  };
}
